<template>
  <row :class="classes">
    <column :size="pinnedColumnSize" class="pinned-column" v-if="hasPinnedColumns && horizontal">
      <scroll-area v-if="listView" ref="pinnedColumn" @scroll.passive="syncScroll($event, 'table')" class="list-view-table horizontal-table">
        <matrix
          fetching-message="No results found."
          allow-horizontal
          v-sticky-header
          ref="table"
          :has-data="entries.length > 0">
          <template #table-header>
            <template v-for="column in pinnedColumns">
              <t-column v-if="pagination && column.sorted_by_ids.length > 0" :size="columnWidthClass(column.x_size)" :key="column.id" is-heading>
                <sort-column :value="column.sorted_by_ids[0]" v-bind="pagination.sortAsJson()" @click="updateSort">
                  {{column.label_title}}
                </sort-column>
              </t-column>
              <t-column is-heading v-else :key="column.id" :size="columnWidthClass(column.x_size)">
                {{column.label_title}}
              </t-column>
            </template>
          </template>
          <template #table-body>
            <list-view-content-element-row
              v-for="entry in entries"
              :is-in-detail-view-edit-mode="isInDetailViewEditMode"
              :key="entry.id"
              :entry="entry"
              :list-view="listView"
              :content-element="listView.firstRow"
              :nested-elements="pinnedColumns">
            </list-view-content-element-row>
          </template>
        </matrix>
      </scroll-area>
    </column>
    <column class="unpinned-columns" :style="unpinnedColumnSize">
      <scroll-area v-if="listView"
                   :id="`${scrollbarId}-1`"
                   ref="table"
                   v-sticky-scrollbar="`${scrollbarId}-2`"
                   :allow-horizontal="horizontal"
                   with-infinite-scroll
                   :class="{'horizontal-table': horizontal}"
                   class="smooth"
                   @scroll.passive="syncScroll($event, 'pinnedColumn')">
        <component :is="infiniteScrollerOrDiv" @loadMore="fetchEntries" :busy="entriesBusy" :fullyLoaded="ObjectModelEntriesFullyLoaded">
          <matrix
            :id="`${scrollbarId}-2`"
            v-sticky-header
            ref="content"
            fetching-message="No results found."
            :allow-horizontal="horizontal"
            :table-body-size="unpinnedTableBodySize"
            class="list-view-table fds"
            :has-data="entries.length > 0">
            <template #table-header>
              <t-prefix v-if="canTakeBulkActions"/>
              <template v-for="column in tableColumns">
                <t-column v-if="pagination && column.sorted_by_ids.length > 0" :size="columnWidthClass(column.x_size)" :key="column.id" is-heading>
                  <sort-column  :value="column.sorted_by_ids[0]" v-bind="pagination.sortAsJson()" @click="updateSort">
                    {{column.label_title}}
                  </sort-column>
                </t-column>
                 <t-column is-heading v-else :key="column.id" :size="columnWidthClass(column.x_size)">
                  {{column.label_title}}
                </t-column>
              </template>
            </template>
            <template #table-body>
              <bulk-action-header
                v-if="canTakeBulkActions"
                :list-view="listView">
              </bulk-action-header>
              <list-view-content-element-row
                v-for="entry in entries"
                :key="entry.id"
                :entry="entry"
                :horizontal="horizontal"
                :is-in-detail-view-edit-mode="isInDetailViewEditMode"
                :list-view="listView"
                :content-element="listView.firstRow"
                :nested-elements="tableColumns">
              </list-view-content-element-row>
            </template>
          </matrix>
        </component>
      </scroll-area>
    </column>
  </row>

</template>

<script lang="ts">
  //this component is used by both the list view and the detail view
  import Vue from "vue";
  import ListViewContentElementRow from "./list-view-content-element-row.vue";
  import ObjectModelFakeEntry from "~/models/objectModelEntries/objectModelFakeEntry";
  import ListViewContentElementDefinition from "~/models/listViews/elements/listViewContentElementDefinition";
  import ListView from "~/models/listViews/listView";
  import ObjectModelEntry from "~/models/objectModelEntries/objectModelEntry";
  import bulkActionMixin from "~/mixins/bulkActionMixin";
  import BulkActionHeader from "./list-view-bulk-action-header.vue"
  import ListViewContentElement from '~/models/listViews/elements/listViewContentElement'
  import PublishedListView from "~/models/listViews/publishedListView";
  import SortColumn from "~/components/sortColumn.vue";
  import Pagination from "~/models/pagination"
  import RouterMixin from "~/mixins/routerMixin";
  import {ListViewFilterLayoutElementType} from "~/enums/listViews/listViewFilterLayoutElementType";
  import storeMapper from "~/mixins/storeMappers/storeMapper";
  import InfiniteScrollerComponent from '~/components/infiniteScroller.vue';
  import HideListViewBus from "../buses/hideListViewBus";
  import AdvancedFilterEventBus from "../buses/advancedFilterEventBus";
  import dashboardDrilldownFiltersMixin from "../mixins/dashboardDrilldownFiltersMixin";
  import scrollToElement from "~/helpers/scrollToElement"
  import RefreshEntryDataBus from '../buses/refreshEntryDataBus';
  import listViewAvailableBulkActionsMixin from '~/mixins/listViewAvailableBulkActionsMixin';
  import Guid from "~/helpers/guid";

  const objectModelEntriesStoreMixin = storeMapper("objectModelEntriesStore", {
    state: ['ObjectModelEntriesFullyLoaded'],
    actions: ["searchEntries:search"]
  });

  export default Vue.extend({
    components: {
      ListViewContentElementRow,
      BulkActionHeader,
      InfiniteScrollerComponent,
      SortColumn
    },
    mixins:[
      bulkActionMixin,
      listViewAvailableBulkActionsMixin,
      RouterMixin,
      objectModelEntriesStoreMixin,
      dashboardDrilldownFiltersMixin,
    ],
    data: function () {
      return {
        loadingEntries: false,
        advancedFilterValidated: null,
        updatePinnedColumnStyle: true,
        pagination: new Pagination(),
        hasPin: true,
        forceNewQuery: false,
        scrollbarId: Guid.create()
      };
    },
    props: {
      isInDetailViewEditMode:{
        type: Boolean,
        default: false
      },
      listView: {
        type: Object as () => ListView | PublishedListView,
        required: true
      },
      entries: {
        type: Array as () => Array<ObjectModelFakeEntry> | Array<ObjectModelEntry>,
        required: true
      },
      isEmbedded: {
        type: Boolean,
        required: true,
      },
      preview: {
        type: Boolean,
        default: false
      },
      hasFilters: {
        type: Boolean,
        default: false,
      },
      horizontal: {
        type: Boolean,
        default: true,
      },
    },
    created() {
      this.registerEventBusListeners();
    },
    methods: {
      columnWidthClass(xSize: number, extraPadding: number = 0): number {
        return this.horizontal ? ListViewContentElementDefinition.getFixedColumnWidthClass(xSize) + extraPadding : null;
      },
      updateSort(prop: string): void {
        scrollToElement({
          $el: $(this.$refs.table.$el),
          offset: -150,
        })
        this.pagination.setSortAndDirection(prop);
        this.pushRouteQuery(this.buildQuery(this.pagination.sortAsJson()));
      },
      syncScroll($event, twin): void {
        if (this.$refs[twin] !== undefined) {
          $(this.$refs[twin].$el).scrollTop($event.target.scrollTop);
        }
      },
      fetchEntries(json = {}): void {
        this.loadingEntries = true;

        const query = Object.assign({}, this.$route.query)
        // this will help increase perceived speed,
        // but it will make things worse if any n+1 queries get added
        // https://www.pivotaltracker.com/story/show/175422636
        query.limit = 20
        this.listView.filterQuery = query

        this.searchEntries({ listView: this.listView, fake: this.preview, forceNewQuery: this.forceNewQuery, ...json })
          .finally(() => {
            HideListViewBus.$emit('hideWhileSearching', false);
            this.forceNewQuery = false;
            this.loadingEntries = false;
            this.triggerVueUpdate();
          });
      },
      updateTable(json?): void {
        this.updatePinnedColumnStyle = true;
        this.fetchEntries(json);
      },
      registerEventBusListeners(): void {
        AdvancedFilterEventBus.onValidated((isValid: boolean) => {
          this.advancedFilterValidated = true;
        });

        const forceUpdateTable = (json?) => {
          this.forceNewQuery = true;
          this.updateTable(json);
        };

        // Entry fetch handled by watcher on query, but advanced filter updates do not reflect in query, so we must trigger manually
        AdvancedFilterEventBus.onEdited(forceUpdateTable);

        if (!this.isEmbedded) {
          RefreshEntryDataBus.onRefresh(() => forceUpdateTable({refresh_index: true}));
        }
      },
    },
    computed: {
      entriesBusy(): boolean {
        return this.awaitingValidation || this.loadingEntries;
      },
      awaitingValidation(): boolean {
        return this.isFormability && !this.advancedFilterValidated;
      },
      hasPinnedColumns() {
        return this.pinnedColumns.length > 0
      },
      pinnedColumns(): ListViewContentElement[] {
        return this.listView.pinnedColumns
      },
      unpinnedColumns(): ListViewContentElement[] {
        return this.listView.unpinnedColumns
      },
      canTakeBulkActions(): boolean {
        return this.listView.viewers_can_take_bulk_actions && (this.isPreview || this.availableBulkActions.length > 0);
      },
      showingBulkActions() {
        return this.canTakeBulkActions && this.entries.length > 0
      },
      scrollSize(): number {
        let filterHeight = 85;
        let filterMargin = 8;
        let containerSize = 200;
        let filtersHeight = 0;

        if(this.hasFilters){
          let isGrid = this.listView.filterElementsArray[0].filter_type === ListViewFilterLayoutElementType.Grid;
          let isStack = this.listView.filterElementsArray[0].filter_type === ListViewFilterLayoutElementType.Stack;
          if( isGrid || isStack || filtersHeight > 385){
            filtersHeight = 385;
          } else {
            filtersHeight = this.listView.filterElementsArray.length * filterHeight
              + this.listView.filterElementsArray.length * filterMargin
              + containerSize;
          }
          return filtersHeight;
        } else { return 160; }
      },
      classes(): object {
        return {
          'has-bulk-actions': this.showingBulkActions,
          'has-pin': this.hasPinnedColumns,
          'details-view': !this.horizontal,
        }
      },
      infiniteScrollerOrDiv(): string {
        return this.isEmbedded ? 'div' : 'infinite-scroller-component';
      },
      tableColumns(): any {
        return this.horizontal ? this.unpinnedColumns : [...this.listView.pinnedColumns, ...this.unpinnedColumns];
      },
      pinnedColumnSize(): number {
        let width: number = this.hasPinnedColumns ? this.columnWidthClass(this.pinnedColumns[0].x_size, 15) : 0;
        if (this.showingBulkActions) { width += 35; } // Bulk Action Checkbox width
        return width;
      },
      unpinnedColumnSize(): string {
        return this.hasPinnedColumns ? `width: calc(100% - ${this.pinnedColumnSize}px); max-width: calc(100% - ${this.pinnedColumnSize}px)` : '';
      },
      unpinnedTableBodySize(): number {
        let columnsWidth = this.tableColumns.map(c => this.columnWidthClass(c.x_size)).reduce((a, b) => a + b)
        let bulkActionCheck = this.showingBulkActions ? 35 : 0;
        return columnsWidth + 20 + bulkActionCheck;
      }
    },
    updated() {
      if (this.listView.viewers_can_take_bulk_actions && this.updatePinnedColumnStyle && !this.loadingEntries) {
        // updates pinned column padding top to accommodate unpinned columns' bulk actions filter or sudden absence thereof (such as after search w/ 0 results)
        const bulkActionFilterHeight = $('.unpinned-columns .table-body .select-filter .filter').outerHeight(true) || 0;
        const unpinnedColumnsPaddingTop = parseInt($('.unpinned-columns .table-body').css('padding-top'), 10);
        $('.pinned-column .table-body').css('padding-top', bulkActionFilterHeight + unpinnedColumnsPaddingTop);
        this.updatePinnedColumnStyle = false;
      }

      // targets all entry rows not marked '.processed' and adjusts their height to match across pinned and unpinned columns, then marks them '.processed'
      const $unprocessedRows = $('.unpinned-columns .table-body > .c-row:not(.processed):not(.prompt)');
      for (let i = 0; i <= $unprocessedRows.length; i++) {
        const $pinnedRow = $('.pinned-column .table-body > .c-row:not(.processed)').first();
        const $unpinnedRow = $('.unpinned-columns .table-body > .c-row:not(.processed)').first();

        if ($pinnedRow[0]) {
          const height = Math.max($pinnedRow.height(), $unpinnedRow.height() || 0);
          $pinnedRow.height(height).addClass('processed');
          $unpinnedRow.height(height).addClass('processed');
        }
      }
    },
    watch: {
      '$route.query': function (newVal, oldVal) {
        this.updateTable();
      },
    },
  });
</script>
